import { ChangeEvent, useCallback, useEffect, useMemo } from "react";
import { defaultSYS1010_R01DtoFilter, SYS1010_R01Filter, useSYS1010_R01Store } from "src/containers/mes/SYS/SYS1010_R01/Store_SYS1010_R01";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { downloadExcel } from "src/utils/blob/blobUtil";

import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { refetchUpdateList } from "src/utils/axios/reFetch";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { downloadExcel_SYS1010_R01, getSYS1010_R01AllCount, getSYS1010_R01MinorCodeList, saveSYS1010_R01 } from "./Api_SYS1010_R01";
import { defaultDetailSYS1010_R01, defaultSYS1010_R01Dto, SYS1010_R01Dto, SYS1010_R01UpdateDto } from "./Dto_SYS1010_R01";
import { AgGridReact } from "ag-grid-react";
import { fetchFilterConfig, gridRefSelectedRowHandler } from "src/utils/grid/GridHandlerContent";
import { validateString } from "src/utils/string/stringUtil";

interface CommonCodeContentProps {
	setMessage: (message: string) => void; // useUiuxStore에서 가져온 setMessage의 타입으로 가정
	gridRef: React.RefObject<AgGridReact<SYS1010_R01Dto> | null>;
	detailGridRef: React.RefObject<AgGridReact<SYS1010_R01Dto> | null>;
	
}

export const Content_SYS1010_R01 = (props: CommonCodeContentProps) => {
	const {
		gridRef,
		detailGridRef,
		setMessage,
	} = props;

	const {
		SYS1010_R01Filter, setSYS1010_R01Filter,
		SYS1010_R01PastFilter, setSYS1010_R01PastFilter,
		selectedMajorSYS1010_R01, setSelectedMajorSYS1010_R01,
		majorSYS1010_R01List, setMajorSYS1010_R01List,
		minorSYS1010_R01List, setMinorSYS1010_R01List, insertMinorSYS1010_R01List,
		originalMinorSYS1010_R01List, setOriginalMinorSYS1010_R01List,
		selectedMinorSYS1010_R01List, setSelectedMinorSYS1010_R01List, removeSelectedMinorSYS1010_R01List,
		deletedMinorSYS1010_R01List, addDeletedMinorSYS1010_R01List, removeDeletedMinorSYS1010_R01List,
		searchMajorSYS1010_R01List,
		// isDeleted,
		setIsDeleted,
		allCount, setAllCount,
		initData,
		setScrollTop,
		setDetailScrollTop,
		detailFetchTrigger, setDetailFetchTrigger,
	} = useSYS1010_R01Store();

	const { setLoadingSpinner } = useLoadingSpinnerStore();
	const { fetchCommonCode } = useCommonCodeStore();

	useFetchActionbarAuth("SYS1010_R01");

	const defaultSYS1010_R01HeaderRow = useCallback(() => {
		return {
			...defaultSYS1010_R01Dto,
			majorCd: selectedMajorSYS1010_R01?.majorCd ?? '',
			inserted: true
		}
	}, [selectedMajorSYS1010_R01]);

	const defaultSYS1010_R01DetailRow = useCallback(() => {
		return {
			...defaultDetailSYS1010_R01,
			majorCd: selectedMajorSYS1010_R01?.majorCd ?? '',
			minorCd: '',
			inserted: true
		}
	}, [selectedMajorSYS1010_R01]);

	const handleFetchDetailList = useCallback(async (major: SYS1010_R01Dto) => {
		const response = await getSYS1010_R01MinorCodeList(major.majorCd);
		const list = response.data.data;
		setMinorSYS1010_R01List(list);
		setOriginalMinorSYS1010_R01List(JSON.parse(JSON.stringify(list)));

		if (list.length === 0) {
			insertMinorSYS1010_R01List([defaultSYS1010_R01DetailRow()]);
			setOriginalMinorSYS1010_R01List([defaultSYS1010_R01DetailRow()]);
		} else {
			detailGridRef.current?.api.ensureIndexVisible(0, 'top');
		}

		showSuccess(`${major.codeNm}의 상세코드 ${list.length}건을 조회하였습니다.`);
		setMessage(`${major.codeNm}의 상세코드 ${list.length}건을 조회하였습니다.`);
		setTimeout(() => setMessage(""), 5000);
	}, [insertMinorSYS1010_R01List, defaultSYS1010_R01HeaderRow, detailGridRef, setMessage]);

	const handleExcelClick = async () => {
		try {
			// 선택된 대분류 코드가 있으면 상세코드 엑셀, 없으면 전체 공통코드 엑셀
			const majorCode = selectedMajorSYS1010_R01?.majorCd;
			const response = await downloadExcel_SYS1010_R01(majorCode);
			
			let blob: Blob;
			
			// 응답 데이터 타입에 따라 처리 방식 결정
			if (Array.isArray(response.data.data)) {
				// byte[] 배열인 경우
				const byteArray = new Uint8Array(response.data.data);
				blob = new Blob([byteArray], { 
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
				});
			} else if (typeof response.data.data === 'string') {
				// Base64 문자열인 경우
				const binaryString = atob(response.data.data);
				const byteArray = new Uint8Array(binaryString.length);
				for (let i = 0; i < binaryString.length; i++) {
					byteArray[i] = binaryString.charCodeAt(i);
				}
				blob = new Blob([byteArray], { 
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
				});
			} else {
				throw new Error('지원하지 않는 데이터 형식입니다.');
			}
			
			// 파일명 설정 (대분류 코드가 있으면 상세코드, 없으면 전체 공통코드)
			const fileName = majorCode 
				? `공통코드_${selectedMajorSYS1010_R01?.codeNm}-excel.xlsx`
				: '공통코드전체-excel.xlsx';
			
			downloadExcel(blob, fileName);
		} catch (error) {
			console.error('엑셀 다운로드 중 오류 발생:', error);
			showWarning('엑셀 다운로드 중 오류가 발생했습니다.');
		}
	};

	// 유효성 검사 함수
	const validateMinorCodeList = (list: SYS1010_R01Dto[]): { isValid: boolean; message: string } => {
		const seenMinorCodes = new Set<string>();

		for (const data of list) {
			if (!validateString(data.minorCd!) || !validateString(data.codeNm!)) {
				return {
					isValid: false,
					message: "새로 생성된 데이터를 채워주세요."
				};
			}

			if (seenMinorCodes.has(data.minorCd)) {
				return {
					isValid: false,
					message: "중복된 코드가 있습니다."
				};
			}
			
			seenMinorCodes.add(data.minorCd);
		}

		return { isValid: true, message: "" };
	};

	const getParams = (filter: SYS1010_R01Filter) => {
		return {
			codeInput: filter.codeInput
		}
	}

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getSYS1010_R01AllCount(getParams(SYS1010_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [SYS1010_R01Filter, setAllCount]);

	const fetchData = useCallback(async () => {
		// refreshMajorCodeList();
		setLoadingSpinner(true);
		setScrollTop(null);
		setDetailScrollTop(null);
		const response = await searchMajorSYS1010_R01List(SYS1010_R01Filter.codeInput, selectedMajorSYS1010_R01);
		const processAllCount = await renewalAllCount();
		if(response.length > 0) {
			gridRefSelectedRowHandler(
				gridRef,
				setSelectedMajorSYS1010_R01,
				() => true,// () => !response.find(value => value.majorCd === selectedMajorSYS1010_R01?.majorCd)
			);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${response.length}/${processAllCount}건)`);
			setTimeout(() => setMessage(""), 5000);
		} else {
			showWarning('데이터가 없습니다.');
		}
		setLoadingSpinner(false);
	}, [SYS1010_R01Filter, gridRef, setMajorSYS1010_R01List, setSelectedMajorSYS1010_R01, setScrollTop]);

	const handleCodeInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		setSYS1010_R01Filter({...SYS1010_R01Filter, codeInput: e.target.value});
	};

	const initContainerData = useCallback(async () => {
		initData();
	}, [initData, setMessage]);

	const handleDeleteClick = async () => {
		// if (isDeleted) {
		if (deletedMinorSYS1010_R01List.length > 0) {
			// 삭제 취소
			removeDeletedMinorSYS1010_R01List();
			setIsDeleted(false);
		} else {
			// 삭제 실행
			const insertedMinorCodeList = minorSYS1010_R01List.filter(value => value.inserted === true).map(value => value.minorCd);
			const _deleted = selectedMinorSYS1010_R01List.filter(value => !insertedMinorCodeList.includes(value));
			addDeletedMinorSYS1010_R01List(_deleted);
			removeSelectedMinorSYS1010_R01List(_deleted);
		}
		await fetchCommonCode();
	};

	const handleSaveClick = async () => {
		if (!selectedMajorSYS1010_R01) return;

		// deletedMinorSYS1010_R01List의 모든 요소를 문자열로 변환하여 비교 준비
		const deletedListAsStrings = deletedMinorSYS1010_R01List.map(id => String(id));

		const savedList = minorSYS1010_R01List
			.filter(value => value.valueIndex != null && !deletedListAsStrings.includes(value.valueIndex.toString()))
			.map((value, index) => ({
				...value,
				inserted: false,
				isModified: false
			}));

		// console.log('savedList : ', JSON.stringify(savedList));

		// 유효성 검사
		const validationResult = validateMinorCodeList(savedList);
		if (!validationResult.isValid) {
			showWarning(validationResult.message);
			return;
		}		

		let processName = 'patch';
		let responseData: SYS1010_R01UpdateDto = {
			majorCodeDto: { ...defaultSYS1010_R01Dto },
			minorCodeDtoList: []
		};

		const response = await saveSYS1010_R01(selectedMajorSYS1010_R01, savedList);
		responseData = response.data.data;

		showSuccess('상세코드 저장이 완료되었습니다.');
		setMessage('상세코드 저장이 완료되었습니다.');
		
		if(selectedMajorSYS1010_R01) {
			refetchUpdateList(processName, responseData.majorCodeDto, selectedMajorSYS1010_R01, 'majorCd', majorSYS1010_R01List, setMajorSYS1010_R01List);
			let minorCodeList = responseData.minorCodeDtoList.sort((a, b) => (a.dispSeq ?? 0) - (b.dispSeq ?? 0));
			
			setMinorSYS1010_R01List(minorCodeList);
			setOriginalMinorSYS1010_R01List(JSON.parse(JSON.stringify(savedList)));
			
			if(responseData.minorCodeDtoList.length === 0) {
				setMinorSYS1010_R01List([defaultSYS1010_R01DetailRow()]);
			}
		}
		// useCommonCodeStore의 모든 공통코드 리스트 업데이트
		await fetchCommonCode();
		removeSelectedMinorSYS1010_R01List();
		removeDeletedMinorSYS1010_R01List();

	};

	// 선택된 공통코드의 상세코드 데이터 로딩
	useEffect(() => {
		if (!detailFetchTrigger) return;
		// 대분류 전환 시 이전 상세 그리드의 삭제 상태를 먼저 초기화
		removeDeletedMinorSYS1010_R01List();
		setIsDeleted(false);
		setSelectedMinorSYS1010_R01List([]);
		setLoadingSpinner(true);

		if (selectedMajorSYS1010_R01) {
			handleFetchDetailList(selectedMajorSYS1010_R01);
		} else {
			setMinorSYS1010_R01List([]);
			setOriginalMinorSYS1010_R01List([]);
		}
		setDetailFetchTrigger(false);
		setLoadingSpinner(false);
	}, [selectedMajorSYS1010_R01, detailFetchTrigger, handleFetchDetailList]);

	return {
		handleExcelClick,
		handleCodeInputChange,
		defaultSYS1010_R01HeaderRow,
		defaultSYS1010_R01DetailRow,
		fetchData,
		initContainerData,
		handleDeleteClick,
		handleSaveClick,
	};
};
